import { useState } from 'react';
import type { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useSearchParams } from 'react-router';
import { Button, Card, Input, Mascot, cn, useI18n } from '@xdocs/ui';
import { passwordResetConfirmInput, passwordResetRequestInput } from '@xdocs/schemas';
import type { PasswordResetConfirmInput, PasswordResetRequestInput } from '@xdocs/schemas';
import { applyServerAlert } from '../auth/applyServerAlert.js';
import { AuthTransportError, requestAuthOp } from '../auth/authTransport.js';
import { safeFieldError, safeT } from '../i18n/safeT.js';
import { optionalProp } from '../lib/optionalProp.js';
import { resolveWsUrl } from '../lib/env.js';
import styles from './AuthCard.module.css';

type RequestState = { kind: 'idle' } | { kind: 'sent'; email: string };

/**
 * `/reset` (F42, design/app mockup 15 pixel parity for the request-link
 * state; the set-new-password state below has no mockup coverage — the
 * extracted prototype only demos the request form — so it's built to match
 * the sibling auth screens' visual language instead: same `Card`/`Mascot`/
 * header pattern as `LoginPage`/`SignupPage`).
 *
 * Two states on one route (spec F42): no `?token=` → request a reset link
 * (neutral "if that email exists…" confirmation, no account enumeration);
 * `?token=…` → set a new password, then route to `/login`.
 */
export function ResetPasswordPage(): ReactElement {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  return token ? <ResetConfirmForm token={token} /> : <ResetRequestForm />;
}

function ResetRequestForm(): ReactElement {
  const { t, locale } = useI18n();
  const [banner, setBanner] = useState<string | null>(null);
  const [state, setState] = useState<RequestState>({ kind: 'idle' });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordResetRequestInput>({
    resolver: zodResolver(passwordResetRequestInput),
    mode: 'onBlur',
    defaultValues: { email: '' },
  });

  async function onSubmit(values: PasswordResetRequestInput): Promise<void> {
    setBanner(null);
    try {
      await requestAuthOp(resolveWsUrl(), 'auth.passwordReset.request', values);
      setState({ kind: 'sent', email: values.email });
    } catch (err) {
      const message =
        err instanceof AuthTransportError ? safeT(locale, err.alert.message) : safeT(locale, 'errors:code.INTERNAL');
      setBanner(message);
    }
  }

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['wrap'], styles['resetWrap'])}>
        <div className={cn(styles['header'], styles['resetHeaderBlock'])}>
          <Mascot pose="thinking" size={80} alt="" className={cn(styles['resetMascotGap'])} />
          <h1 className={cn(styles['title'], styles['resetTitle'])}>{t('auth:resetPassword.title')}</h1>
          <p className={cn(styles['subtitle'], styles['resetSubtitleGap'])}>{t('auth:resetPassword.subtitle')}</p>
        </div>

        <Card className={cn(styles['cardBox'])}>
          {banner && (
            <p role="alert" className={cn(styles['banner'])}>
              {banner}
            </p>
          )}

          {state.kind === 'sent' ? (
            <p role="status" className={cn(styles['helperText'])}>
              {t('auth:resetPassword.sent', { email: state.email })}
            </p>
          ) : (
            <>
              <form
                className={cn(styles['form'])}
                onSubmit={(event) => void handleSubmit(onSubmit)(event)}
                noValidate
              >
                <Input
                  type="email"
                  autoComplete="email"
                  label={t('auth:login.email.label')}
                  placeholder={t('auth:resetPassword.email.placeholder')}
                  containerClassName={cn(styles['fieldLabel'])}
                  className={cn(styles['fieldInput'], styles['resetInputLineHeight'])}
                  {...optionalProp('error', safeFieldError(locale, errors.email?.message))}
                  {...register('email')}
                />
                <Button
                  type="submit"
                  variant="primary"
                  className={cn(styles['fullWidthAction'], styles['primarySubmit'], styles['resetSubmitSize'])}
                  state={isSubmitting ? 'loading' : 'idle'}
                >
                  {t('auth:resetPassword.submit')}
                </Button>
              </form>
              <p className={cn(styles['helperText'], styles['resetDisclaimer'])}>
                {t('auth:resetPassword.disclaimer')}
              </p>
            </>
          )}
        </Card>

        <p className={cn(styles['footer'], styles['footerBlock'])}>
          <Link to="/login" className={cn(styles['resetBackLink'])}>
            {t('auth:resetPassword.backToLogin')}
          </Link>
        </p>
      </div>
    </div>
  );
}

function ResetConfirmForm({ token }: { token: string }): ReactElement {
  const { t, locale } = useI18n();
  const [banner, setBanner] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PasswordResetConfirmInput>({
    resolver: zodResolver(passwordResetConfirmInput),
    mode: 'onBlur',
    defaultValues: { token, newPassword: '', confirmPassword: '' },
  });

  async function onSubmit(values: PasswordResetConfirmInput): Promise<void> {
    setBanner(null);
    try {
      await requestAuthOp(resolveWsUrl(), 'auth.passwordReset.confirm', values);
      setSucceeded(true);
    } catch (err) {
      const message = applyServerAlert(err, setError, locale);
      if (message) setBanner(message);
    }
  }

  return (
    <div className={cn(styles['page'])}>
      <Card className={cn(styles['card'])}>
        <div className={cn(styles['header'])}>
          <Mascot pose={succeeded ? 'celebrating' : 'thinking'} size={80} alt="" />
          <h1 className={cn(styles['title'])}>{t('auth:resetPassword.confirm.title')}</h1>
          {!succeeded && <p className={cn(styles['subtitle'])}>{t('auth:resetPassword.confirm.subtitle')}</p>}
        </div>

        {banner && (
          <p role="alert" className={cn(styles['banner'])}>
            {banner}
          </p>
        )}

        {succeeded ? (
          <>
            <p role="status">{t('auth:resetPassword.confirm.success')}</p>
            <Link to="/login">
              <Button variant="primary">{t('auth:resetPassword.confirm.successCta')}</Button>
            </Link>
          </>
        ) : (
          <form className={cn(styles['form'])} onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
            <input type="hidden" {...register('token')} />
            <Input
              type="password"
              autoComplete="new-password"
              label={t('auth:resetPassword.confirm.newPassword.label')}
              required
              {...optionalProp('error', safeFieldError(locale, errors.newPassword?.message))}
              {...register('newPassword')}
            />
            <Input
              type="password"
              autoComplete="new-password"
              label={t('auth:resetPassword.confirm.confirmPassword.label')}
              required
              {...optionalProp('error', safeFieldError(locale, errors.confirmPassword?.message))}
              {...register('confirmPassword')}
            />
            <Button type="submit" variant="primary" state={isSubmitting ? 'loading' : 'idle'}>
              {t('auth:resetPassword.confirm.submit')}
            </Button>
          </form>
        )}

        <p className={cn(styles['footer'])}>
          <Link to="/login">{t('auth:resetPassword.backToLogin')}</Link>
        </p>
      </Card>
    </div>
  );
}
